import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    ConfigConstants,
    ConfigService,
    LoadingScreenComponent,
    LoadingScreenModel,
    NotificationInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { EMPTY, from, of } from 'rxjs';
import { catchError, finalize, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { BaseFormsListComponent } from '../../components/base-forms-list/base-forms-list.component';
import { FormsListComponentInstanceToken } from '../../components/forms-list/forms-list.component';
import { NotificationInstanceToken } from '../../core/interfaces/notification.interface';
import { ActionUtilitiesInstanceToken, ActionUtilitiesInterface } from '../../services/action-utilities.service';
import { FormsService } from '../../services/forms.service';
import { FormsConstants } from '../../types/forms-constants';
import { emptyAction } from '../actions/empty.actions';
import { loadEquipmentSet, loadFormListSuccess } from '../actions/form-list.actions';
import { navigateToForm } from '../actions/form.actions';
import { tryToPerformPaceFormsQuickAccess } from '../actions/last-opened-form.actions';
import { getLastOpenedFormByNotificationFactory } from '../selectors/last-opened-form.selector';
import { FormsState } from '../state/pace-forms.state';

// General purpose form effects
@Injectable()
export class FormEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store<FormsState>,
        private readonly formsService: FormsService,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        private readonly configService: ConfigService,
        private readonly modalService: BsModalService,
        private readonly router: Router,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        @Inject(FormsListComponentInstanceToken) private readonly formsListComponent: BaseFormsListComponent,
        @Inject(ActionUtilitiesInstanceToken) private readonly actionUtilities: ActionUtilitiesInterface,
    ) {}

    public logActions$ = createEffect(
        () =>
            this.actions$.pipe(
                tap((action) => {
                    this.log.debug(`FormEffects: logActions$: ${this.actionUtilities.getLogString(action)}`);
                }),
            ),
        // dispatch: false will prevent the effects pipeline from entering an infinite loop
        // https://ngrx.io/guide/effects/lifecycle#non-dispatching-effects
        { dispatch: false },
    );

    public navigateToForm$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(navigateToForm),
                switchMap((action) => {
                    const notification = this.notificationService.getNotificationStateByIdentifier(action.notification);
                    if (typeof notification === 'undefined') {
                        throw new Error(
                            `FormEffects: navigateToForm$: Could not load notification for identifier ${JSON.stringify(
                                action.notification,
                            )}`,
                        );
                    }

                    const routeParams = [
                        ConfigConstants.routeUrlForms2,
                        action.notification.frontId,
                        action.notification.qmNum,
                        action.notification.maNum,
                        action.form.materialId,
                        action.form.equipmentId,
                        action.form.formId,
                        notification.languageId.length > 0 ? notification.languageId : 'EN', // In production the languageId should always exist, but in testing it might be missing
                        action.status,
                    ];

                    // changing the route will trigger form loading: PACE Forms uses an activation guard to dispatch the loadForm event
                    return from(this.router.navigate(routeParams)).pipe(
                        catchError((error) => {
                            this.log.error(`FormEffects: navigateToForm$: error in pipeline: ${JSON.stringify(error)}`);
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    public tryToPerformPaceFormsQuickAccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(tryToPerformPaceFormsQuickAccess),
            switchMap((action) =>
                this.store.select(getLastOpenedFormByNotificationFactory({ notification: action.notification })).pipe(
                    take(1),
                    mergeMap((lastOpenedForm) => {
                        const loadingScreenModel = new LoadingScreenModel(
                            <string>this.translate.instant('LOADING_SCREEN_TITLE'),
                        );
                        const loadingScreenInstance = this.modalService.show(LoadingScreenComponent, {
                            backdrop: 'static',
                            class: 'modal-sm',
                            initialState: {
                                model: loadingScreenModel,
                            },
                            ariaLabelledBy: 'loading-screen-modal-title',
                        });

                        return loadingScreenModel.loadingScreenDisplayed$.pipe(
                            take(1),
                            mergeMap(() =>
                                this.formsService.getFormList(
                                    action.materialNr,
                                    action.equipmentId,
                                    action.notification,
                                    false,
                                ),
                            ),
                            mergeMap((formList) => {
                                const actions = [];
                                // If there is only one form for a notification or a single locked form, then that form will be opened directly.
                                // In which case, the current notification number will not be added in the form list(NotificationFormListState).
                                // So, we are updating the list by calling the loadFormListSuccess action.
                                actions.push(loadFormListSuccess({ formList }));
                                actions.push(
                                    loadEquipmentSet({
                                        notificationIdentifier: action.notification,
                                        mainEquipment: { matnr: action.materialNr, sernr: action.serialNr },
                                    }),
                                );
                                if (typeof lastOpenedForm !== 'undefined') {
                                    actions.push(
                                        navigateToForm({
                                            form: lastOpenedForm,
                                            notification: action.notification,
                                            status: FormsConstants.FORMSTATUS.LOCK,
                                        }),
                                    );
                                } else {
                                    // get a list of entries that the user has locked
                                    const lockedEntries = formList.entries.filter(
                                        (entry) => entry.lockedBy === this.configService.getUserFrontId(),
                                    );
                                    // if the user already locked a single form, we'll directly open it
                                    if (lockedEntries.length === 1) {
                                        actions.push(
                                            navigateToForm({
                                                form: lockedEntries[0].id,
                                                notification: formList.notification,
                                                status: FormsConstants.FORMSTATUS.LOCK,
                                            }),
                                        );
                                    }

                                    const notLockedEntries = formList.entries.filter((entry) => entry.lockedBy === '');
                                    // if there is only one form that the user could lock and edit and no form has been locked so far,
                                    // then we can edit it
                                    if (lockedEntries.length === 0 && notLockedEntries.length === 1) {
                                        actions.push(
                                            navigateToForm({
                                                form: notLockedEntries[0].id,
                                                notification: formList.notification,
                                                status: FormsConstants.FORMSTATUS.LOCK,
                                            }),
                                        );
                                    }
                                }

                                if (actions.length < 3) {
                                    throw new Error(`Could not determine form to open`);
                                }
                                return actions;
                            }),
                            catchError(() => {
                                this.modalService.show(
                                    this.formsListComponent as unknown as new (
                                        ...args: unknown[]
                                    ) => BaseFormsListComponent,
                                    {
                                        backdrop: 'static',
                                        class: 'modal-lg',
                                        initialState: {
                                            notificationIdentifier: action.notification,
                                            useFormListCache: true,
                                        } as BaseFormsListComponent,
                                        ariaLabelledBy: 'forms-list-modal-title',
                                    },
                                );
                                return of(emptyAction());
                            }),
                            finalize(() => loadingScreenInstance.hide()),
                        );
                    }),
                    catchError((error) => {
                        this.log.error(
                            `FormEffects: tryToPerformPaceFormsQuickAccess$: error in pipeline: ${JSON.stringify(
                                error,
                            )}`,
                        );
                        return EMPTY;
                    }),
                ),
            ),
        ),
    );
}
